import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Check, ArrowRight, Clock, Building2, Users, FileText, BarChart3, Shield, Terminal, Globe, X, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API } from '../config';

const tiers = [
  {
    id: 'solo',
    name: 'Solo',
    audience: 'Trucking Companies',
    price: '$39',
    period: '/mo',
    description: 'Manage your own claims with a dedicated workspace for your company.',
    badge: null,
    highlight: false,
    cta: 'Start Free Trial',
    ctaLink: '/register',
    isWaitlist: false,
    base: [
      '1 company included',
      '1 admin, 2 users, unlimited viewers',
      'Unlimited claims & documents',
      'All features unlocked',
    ],
    addons: [
      { label: 'Extra company', price: '$29/mo' },
      { label: 'Extra admin', price: '$11/mo' },
      { label: 'Extra user', price: '$9/mo' },
    ],
    features: [
      { icon: FileText, text: 'Multi-step intake form' },
      { icon: BarChart3, text: 'Dashboard & analytics' },
      { icon: Building2, text: 'Company management' },
      { icon: Users, text: 'Team & role management' },
      { icon: Shield, text: 'Audit log & recycle bin' },
      { icon: Clock, text: 'Document tracking' },
    ],
  },
  {
    id: 'provider',
    name: 'Provider',
    audience: 'Claims Service Providers',
    price: '$99',
    period: '/mo',
    description: 'Manage claims across multiple client organizations from a single platform.',
    badge: null,
    highlight: false,
    cta: 'Start Free Trial',
    ctaLink: '/register?plan=provider',
    isWaitlist: false,
    base: [
      '3 organizations, 6 companies',
      '1 admin, 2 users, unlimited viewers',
      'Unlimited claims & documents',
      'All Solo features included',
    ],
    addons: [
      { label: 'Extra organization', price: '$30/mo' },
      { label: 'Extra company', price: '$9/mo' },
      { label: 'Extra admin', price: '$7/mo' },
      { label: 'Extra user', price: '$5/mo' },
    ],
    features: [
      { icon: Globe, text: 'Multi-org management' },
      { icon: Users, text: 'Org invite links' },
      { icon: Building2, text: 'Per-org branding' },
      { icon: Shield, text: 'Cross-org visibility' },
      { icon: BarChart3, text: 'Client dashboards' },
      { icon: FileText, text: 'User overlap across orgs' },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    audience: 'Insurance Providers',
    price: '$349',
    period: '/mo',
    description: 'Read-only visibility and combined reporting across all insured organizations.',
    badge: 'Coming Soon',
    highlight: false,
    cta: 'Join Waitlist',
    ctaLink: null,
    isWaitlist: true,
    base: [
      '5 organizations included',
      '3 viewer seats included',
      'Read-only access across all orgs',
      'All Provider features included',
    ],
    addons: [
      { label: 'Extra organization', price: '$35/mo' },
      { label: 'Extra viewer', price: '$20/mo' },
    ],
    features: [
      { icon: BarChart3, text: 'Combined dashboard' },
      { icon: FileText, text: 'Cross-org reports' },
      { icon: Building2, text: 'Side-by-side comparison' },
      { icon: Terminal, text: 'API access (Tier 2+)' },
      { icon: Shield, text: 'Custom data retention' },
      { icon: Globe, text: 'Enterprise support' },
    ],
    tiers_note: 'Also available: National ($749/mo) & Custom tiers',
  },
];

const trialFeatures = [
  '30-day free trial on all plans',
  'No credit card required',
  'All features unlocked during trial',
  'Cancel anytime',
];

function WaitlistModal({ plan, onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { name, email, company, plan });
      if (res.data.already_exists) {
        toast.info("You're already on the waitlist!");
      } else {
        toast.success("You've been added to the waitlist!");
      }
      setSubmitted(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="waitlist-modal-backdrop">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md relative" onClick={(e) => e.stopPropagation()} data-testid="waitlist-modal">
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-testid="waitlist-modal-close">
          <X className="w-5 h-5" />
        </button>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-6" data-testid="waitlist-success">
              <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-teal-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">You're on the list!</h3>
              <p className="text-sm text-gray-500 mt-2">We'll notify you when the {plan === 'provider' ? 'Provider' : 'Enterprise'} plan launches. Keep an eye on your inbox.</p>
              <Button onClick={onClose} className="mt-6 bg-teal-500 hover:bg-teal-600 text-white" data-testid="waitlist-done-btn">
                Done
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Join the {plan === 'provider' ? 'Provider' : 'Enterprise'} Waitlist</h3>
                  <p className="text-xs text-gray-500">Be the first to know when it launches</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="waitlist-name-input"
                />
                <Input
                  type="email"
                  placeholder="Work email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="waitlist-email-input"
                />
                <Input
                  placeholder="Company name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  data-testid="waitlist-company-input"
                />
                <Button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white h-10 font-semibold mt-2"
                  data-testid="waitlist-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining...</>
                  ) : (
                    <>Join Waitlist <ArrowRight className="w-4 h-4 ml-1.5" /></>
                  )}
                </Button>
              </form>
              <p className="text-[11px] text-gray-400 mt-3 text-center">We'll only email you about the {plan === 'provider' ? 'Provider' : 'Enterprise'} plan launch. No spam.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [waitlistPlan, setWaitlistPlan] = useState(null);

  return (
    <div className="min-h-screen bg-gray-100" data-testid="pricing-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2">
            <img src="/logo.png" alt="CMS" className="w-8 h-8" />
            <span className="text-lg font-bold text-gray-900">CMS</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 transition-colors" data-testid="pricing-login-link">
              Sign In
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 pt-12 pb-8 md:pt-16 md:pb-10 text-center">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight" data-testid="pricing-title">
          Simple, transparent pricing
        </h1>
        <p className="text-base md:text-lg text-gray-500 mt-3 max-w-2xl mx-auto">
          From single trucking companies to enterprise insurance providers. Start free, scale as you grow.
        </p>

        {/* Trial badges */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
          {trialFeatures.map((feat) => (
            <span key={feat} className="inline-flex items-center gap-1.5 text-xs md:text-sm text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-3 py-1">
              <Check className="w-3.5 h-3.5" />
              {feat}
            </span>
          ))}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const hoverClass = tier.id === 'solo'
              ? 'hover:ring-2 hover:ring-teal-500 hover:shadow-lg'
              : tier.id === 'provider'
              ? 'hover:ring-2 hover:ring-blue-500 hover:shadow-lg'
              : 'hover:ring-2 hover:ring-gray-400 hover:shadow-lg';
            return (
            <Card
              key={tier.id}
              className={`relative flex flex-col transition-all duration-200 shadow-sm ${hoverClass}`}
              data-testid={`pricing-card-${tier.id}`}
            >
              {tier.isWaitlist && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gray-400 text-white text-[11px] font-semibold px-3 py-1 rounded-full" data-testid={`badge-${tier.id}`}>
                    Coming Soon
                  </span>
                </div>
              )}

              <CardHeader className="pb-2 pt-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{tier.audience}</p>
                <CardTitle className="text-xl font-bold text-gray-900 mt-1">{tier.name}</CardTitle>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-sm text-gray-500">{tier.period}</span>
                </div>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">{tier.description}</p>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col pt-0">
                {/* CTA */}
                {tier.isWaitlist ? (
                  <Button
                    className="w-full h-10 font-semibold mt-4 bg-gray-400 hover:bg-gray-500 text-white"
                    onClick={() => setWaitlistPlan(tier.id)}
                    data-testid={`cta-${tier.id}`}
                  >
                    {tier.cta}
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                ) : (
                  <Link to={tier.ctaLink} className="block mt-4">
                    <Button
                      className={`w-full h-10 font-semibold ${
                        tier.id === 'solo'
                          ? 'bg-teal-500 hover:bg-teal-600 text-white'
                          : tier.id === 'provider'
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      }`}
                      data-testid={`cta-${tier.id}`}
                    >
                      {tier.cta}
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                )}

                {/* Try Demo button — only for Solo and Provider */}
                {(tier.id === 'solo' || tier.id === 'provider') && (
                  <Link to={`/login?demo=${tier.id}`} className="block mt-2">
                    <Button
                      variant="outline"
                      className={`w-full h-9 text-sm font-medium ${
                        tier.id === 'solo'
                          ? 'border-teal-300 text-teal-700 hover:bg-teal-50'
                          : 'border-blue-300 text-blue-700 hover:bg-blue-50'
                      }`}
                      data-testid={`demo-${tier.id}`}
                    >
                      Try {tier.name} Demo
                    </Button>
                  </Link>
                )}

                {/* Base inclusions */}
                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Includes</p>
                  <ul className="space-y-2.5">
                    {tier.base.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Add-ons */}
                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Add-ons</p>
                  <div className="space-y-2">
                    {tier.addons.map((addon) => (
                      <div key={addon.label} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{addon.label}</span>
                        <span className="font-medium text-gray-900">{addon.price}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Key Features</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {tier.features.map((feat) => (
                      <div key={feat.text} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <feat.icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        {feat.text}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tiers note */}
                {tier.tiers_note && (
                  <p className="mt-4 text-xs text-gray-400 text-center italic">{tier.tiers_note}</p>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-200 py-6">
        <p className="text-center text-xs text-gray-400">CMS By All About Claims. All rights reserved.</p>
      </div>

      {/* Waitlist Modal */}
      {waitlistPlan && (
        <WaitlistModal plan={waitlistPlan} onClose={() => setWaitlistPlan(null)} />
      )}
    </div>
  );
}
